import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Badge, Row, Col, Form, Spinner, Modal } from "react-bootstrap";
import PageWrapperForm from "../components/PageWrapperForm";
import { paidService } from "../service/paidService";
import { websitePlanService } from "../service/websitePlanService";
import PaidServiceFormModal from "./components/PaidServiceFormModal";

const PaidServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [plansMap, setPlansMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await websitePlanService.getAll();
      if (res?.data && Array.isArray(res.data)) {
        const map = {};
        res.data.forEach((p) => {
          const duration = p.duration ? ` (${p.duration}M)` : "";
          map[p.id] = `${p.plan_name}${duration}`;
        });
        setPlansMap(map);
      }
    } catch (e) {
      console.warn("Could not fetch website plans list:", e);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paidService.getAll();
      const list = Array.isArray(res?.data) ? res.data : [];
      setServices(list);
    } catch (err) {
      console.warn("Paid services endpoint not returning data yet:", err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchServices();
  }, [fetchPlans, fetchServices]);

  const handleSave = async (data) => {
    try {
      await paidService.addOrUpdate(data);
      setShowModal(false);
      await fetchServices();
    } catch (err) {
      console.error("Save failed:", err);
      setShowModal(false);
      await fetchServices();
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await paidService.remove(deleteItem.id);
      fetchServices();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setShowDeleteModal(false);
      setDeleteItem(null);
    }
  };

  const filteredServices = services.filter((s) => {
    const planName = plansMap[s.website_plan] || s.plan_name || s.website_plan || "";
    return (
      (s.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      planName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <main>
      <div className="container-fluid">
        {/* Custom Header Layout */}
        <div className="row m-1 mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="main-title fw-bold" style={{ color: "#1a1a1a", fontSize: "1.8rem", marginBottom: "4px" }}>Paid Services Management</h4>
              <div className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13.5px", fontWeight: "500" }}>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2", display: "flex", alignItems: "center" }}>
                  <i className="ph-duotone ph-list-dashes f-s-16"></i> <span className="ms-1">Website Plan</span>
                </a>
                <span style={{ color: "#a0a5b1" }}>/</span>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2" }}>
                  Paid Services Management
                </a>
              </div>
            </div>

            <div>
              <button
                className="btn btn-primary d-flex align-items-center justify-content-center px-4"
                onClick={() => {
                  setSelectedItem(null);
                  setShowModal(true);
                }}
                style={{
                  backgroundColor: "#7e57c2", borderColor: "#7e57c2", borderRadius: "8px", height: "44px", width: "fit-content", minWidth: "160px"
                }}
              >
                <i className="iconoir-plus-circle me-2" style={{ fontSize: "20px" }}></i>
                <span className="fw-medium text-nowrap">Add</span>
              </button>
            </div>
          </div>
        </div>

        <div className="row m-1">
          <div className="col-12">
      <Card className="shadow-sm border-0 bg-white text-dark" data-bs-theme="light">
        <Card.Body>
          <Row className="mb-3 align-items-center">
            <Col md={4} sm={8}>
              <Form.Control
                type="text"
                placeholder="Search service by name or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            {loading && (
              <Col auto>
                <Spinner animation="border" size="sm" className="text-primary" />
              </Col>
            )}
          </Row>

          <div className="table-responsive">
            <Table hover striped className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Service Name</th>
                  <th>Website Plan</th>
                  <th>Amount (₹)</th>
                  <th>Discount (%)</th>
                  <th>Final Price</th>
                  <th>Optional 1</th>
                  <th>Optional 2</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.length > 0 ? (
                  filteredServices.map((item, index) => {
                    const price = parseFloat(item.amount || 0);
                    const disc = parseFloat(item.discount || 0);
                    const finalPrice = price - (price * disc) / 100;
                    const displayPlanName =
                      plansMap[item.website_plan] || item.plan_name || item.website_plan || "-";

                    return (
                      <tr key={item.id || index}>
                        <td>{index + 1}</td>
                        <td className="fw-semibold text-primary">{item.name}</td>
                        <td>
                          <Badge bg="light" text="dark" className="border">
                            {displayPlanName}
                          </Badge>
                        </td>
                        <td>₹{price.toFixed(2)}</td>
                        <td>{disc > 0 ? `${disc}%` : "0%"}</td>
                        <td className="fw-bold text-success">₹{finalPrice.toFixed(2)}</td>
                        <td>{item.optional1 || "-"}</td>
                        <td>{item.optional2 || "-"}</td>
                        <td>
                          <Badge bg={String(item.status) === "true" ? "success" : "danger"}>
                            {String(item.status) === "true" ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-success icon-btn b-r-4"
                              type="button"
                              title="Edit"
                              onClick={() => {
                                setSelectedItem(item);
                                setShowModal(true);
                              }}
                            >
                              <i className="iconoir-edit"></i>
                            </button>
                            <button
                              className="btn btn-danger icon-btn b-r-4"
                              type="button"
                              title="Delete"
                              onClick={() => handleDeleteClick(item)}
                            >
                              <i className="iconoir-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      {loading ? "Loading services..." : "No paid services found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Edit / Add Modal */}
      <PaidServiceFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleSave}
        initialData={selectedItem}
      />

      {/* Modern Confirmation Modal for Delete */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-danger text-white py-2">
          <Modal.Title className="fs-6 text-white m-0">Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-danger-subtle text-danger d-flex align-items-center justify-content-center me-3 flex-shrink-0"
              style={{ width: 48, height: 48 }}
            >
              <i className="iconoir-trash fs-4"></i>
            </div>
            <div>
              <h6 className="fw-bold mb-1">Delete Paid Service?</h6>
              <p className="text-muted mb-0 small">
                Are you sure you want to delete{" "}
                <strong>{deleteItem?.name || "this service"}</strong>? This action cannot be undone.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="light" size="sm" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PaidServicesManagement;
